<!-- Component Header with Loading State and Refresh Button -->
<div class="category-analysis-header">
  <div class="header-content">
    <h2 class="component-title">
      <mat-icon>category</mat-icon>
      {{ title }}
    </h2>
    <div class="header-actions">
      @if (isLoading) {
        <mat-spinner diameter="24"></mat-spinner>
        <span class="loading-text">Loading...</span>
      } @else {
        <button mat-icon-button (click)="refreshData()" matTooltip="Refresh Data">
          <mat-icon>refresh</mat-icon>
        </button>
      }
    </div>
  </div>
  @if (error) {
    <div class="error-message">
      <mat-icon>error</mat-icon>
      <div class="error-content">
        <span class="error-text">{{ error }}</span>
        <button mat-button color="primary" (click)="refreshData()" [disabled]="isLoading">
          <mat-icon>refresh</mat-icon>
          Retry
        </button>
      </div>
    </div>
  }
</div>

<!-- Main Content -->
@if (categoryAnalysisResponse && hasData()) {
<div class="category-analysis-container">
  <!-- Top Row: Score Widget and Chart -->
  <div class="top-row">
    <!-- Composite Score Widget -->
    <app-composite-score-widget
      [compositeScore]="compositeScore"
      [title]="'AI Category Score'"
      [size]="'medium'">
    </app-composite-score-widget>

    <!-- AI Category Insights Chart -->
    <div class="chart-section">
      <div class="chart-header">
        <div class="chart-title-section">
          <h4 class="chart-title">AI Category Insights</h4>
          <p class="chart-subtitle">{{ getChartSubtitle() }}</p>
        </div>
      </div>

      <!-- AI Category Insights Chart -->
      <div class="chart-mobile-wrapper">
        <div *ngIf="comboChartData && comboChartData.length > 0" class="combo-chart-wrapper">
          <app-bar-line-combo-chart
            [view]="view"
            [scheme]="comboBarScheme"
            [colorSchemeLine]="lineChartScheme"
            [results]="comboChartData"
            [lineChart]="[]"
            [barBrandName]="'Category Analysis'"
            [animations]="true"
            [tooltipDisabled]="false"
            [gradient]="false"
            [xAxis]="true"
            [yAxis]="true"
            [legend]="true"
            [legendTitle]="'Categories'"
            [legendPosition]="'right'"
            [showGridLines]="true"
            [showXAxisLabel]="true"
            [showYAxisLabel]="true"
            [xAxisLabel]="'Subject Areas'"
            [yAxisLabel]="'Score'"
            [noBarWhenZero]="true"
            [wrapTicks]="true"
            (select)="onSelect($event)"
            (activate)="onActivate($event)"
            (deactivate)="onDeactivate($event)">
          </app-bar-line-combo-chart>
        </div>
      </div>
    </div>
  </div>

  <!-- Second Row: University Information -->
  @if (infoCards && infoCards.length > 0) {
  <div class="info-cards-section">
    <h3>University Information</h3>
    <div class="info-cards-grid">
      @for (card of infoCards; track card.title) {
      <div class="info-card">
        <div class="info-card-title">{{ card.title }}</div>
        <div class="info-card-value">{{ card.value }}</div>
      </div>
      }
    </div>
  </div>
  }

  <!-- Third Row: AI Category Comparison Table -->
  @if (tableRows && tableRows.length > 0) {
  <div class="comparison-table-section">
    <mat-card class="comparison-table-card">
      <mat-card-header>
        <mat-card-title>AI Visibility Table by Category</mat-card-title>
        <mat-card-subtitle>Detailed category analysis across universities</mat-card-subtitle>
      </mat-card-header>
      <mat-card-content>
        <div class="table-container">
          <table class="ai-visibility-comparison-table">
            <thead>
              <tr>
                <th class="university-column">Category</th>
                <th class="metric-column">Score</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let row of tableRows; let i = index" [class.first-row]="i === 0">
                <td class="university-cell">
                  <div class="university-name">{{ row['category'] }}</div>
                </td>
                <td class="metric-cell">
                  <div class="metric-value">{{ row['score'] || 0 | number:'1.0-0' }}</div>
                  <div class="metric-bar">
                    <div class="metric-fill" [style.width.%]="row['score'] || 0"></div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </mat-card-content>
    </mat-card>
  </div>
  }

  <!-- Fourth Row: Category Demand Chart -->
  @if (categoryDemandData && categoryDemandData.length > 0) {
  <div class="category-demand-section">
    <div class="chart-header">
      <h3>Category AI Performance (Demanding Categories)</h3>
    </div>
    <div class="chart-mobile-wrapper">
      <div class="combo-chart-wrapper">
        <app-bar-line-combo-chart
          [view]="view"
          [scheme]="comboBarScheme"
          [results]="categoryDemandData"
          [animations]="true"
          [tooltipDisabled]="false"
          [gradient]="false"
          [xAxis]="true"
          [yAxis]="true"
          [legend]="false"
          [showGridLines]="true"
          [showXAxisLabel]="true"
          [showYAxisLabel]="true"
          [xAxisLabel]="'Categories'"
          [yAxisLabel]="'Rating'"
          [wrapTicks]="true"
          (select)="onSelect($event)"
          (activate)="onActivate($event)"
          (deactivate)="onDeactivate($event)">
        </app-bar-line-combo-chart>
      </div>
    </div>
  </div>
  }

  <!-- Fifth Row: Subject Demand Analysis Chart -->
  @if (subjectDemandData && subjectDemandData.length > 0) {
  <div class="subject-demand-section">
    <div class="chart-header">
      <h3>Subject Demand Analysis</h3>
    </div>
    <div class="chart-mobile-wrapper">
      <div class="combo-chart-wrapper">
        <app-bar-line-combo-chart
          [view]="view"
          [scheme]="comboBarScheme"
          [results]="subjectDemandData"
          [animations]="true"
          [tooltipDisabled]="false"
          [gradient]="false"
          [xAxis]="true"
          [yAxis]="true"
          [legend]="false"
          [showGridLines]="true"
          [showXAxisLabel]="true"
          [showYAxisLabel]="true"
          [xAxisLabel]="'Metrics'"
          [yAxisLabel]="'Value'"
          [wrapTicks]="true"
          (select)="onSelect($event)"
          (activate)="onActivate($event)"
          (deactivate)="onDeactivate($event)">
        </app-bar-line-combo-chart>
      </div>
    </div>
  </div>
  }

  <!-- Sixth Row: Strengths and Areas for Improvement -->
  @if (recommendations) {
  <div class="strengths-improvement-section">
    <mat-card class="strengths-improvement-card">
      <mat-card-header>
        <mat-card-title>Strengths and Areas for Improvement</mat-card-title>
        <mat-card-subtitle>Comprehensive analysis of university performance</mat-card-subtitle>
      </mat-card-header>
      <mat-card-content>
        <div class="university-analysis-grid">
          <!-- Single University Analysis -->
          <div class="single-university-analysis">
            <div class="analysis-card">
              <div class="university-header">
                <h4>{{ getUniversityName() || 'University' }}</h4>
              </div>

              <div class="strengths-section">
                <div class="section-header">
                  <div class="section-indicator strengths-indicator"></div>
                  <h5>Strengths:</h5>
                </div>
                <ul class="analysis-list">
                  @for (strength of recommendations.strengths || []; track strength) {
                  <li>{{ strength }}</li>
                  }
                </ul>
              </div>

              <div class="improvement-section">
                <div class="section-header">
                  <div class="section-indicator improvement-indicator"></div>
                  <h5>Areas for Improvement:</h5>
                </div>
                <ul class="analysis-list">
                  @for (weakness of recommendations.weaknesses || []; track weakness) {
                  <li>{{ weakness }}</li>
                  }
                </ul>
              </div>
            </div>
          </div>
        </div>
      </mat-card-content>
    </mat-card>
  </div>
  }

  <!-- Seventh Row: Citations Sources -->
  @if (citations && citations.length > 0) {
  <div class="citations-section">
    <app-common-sources
      [evidenceSources]="citations"
      [title]="'AI Visibility Sources'"
      [showTitle]="true">
    </app-common-sources>
  </div>
  }

</div>
}
